import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import Avatar from 'flarum/common/components/Avatar';
import CommentPost from 'flarum/forum/components/CommentPost';
import UserCard from 'flarum/forum/components/UserCard';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import PostUser from 'flarum/forum/components/PostUser';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import UserControls from 'flarum/forum/utils/UserControls';
import ShopPage from './components/ShopPage';
import DecorationsPage from './components/DecorationsPage';
import TradesPage from './components/TradesPage';
import UserTradesPage from './components/UserTradesPage';
import UserPage from 'flarum/forum/components/UserPage';
import AwardPointsModal from './components/AwardPointsModal';
import PointsManualNotification from './components/PointsManualNotification';
import PointsEarnedNotification from './components/PointsEarnedNotification';
import TierClaimedNotification from './components/TierClaimedNotification';
import ItemGrantedNotification from './components/ItemGrantedNotification';
import TradeRequestedNotification from './components/TradeRequestedNotification';
import TradeAcceptedNotification from './components/TradeAcceptedNotification';
import TradeCompletedNotification from './components/TradeCompletedNotification';
import TradeModal from './components/TradeModal';
import { applyAvatarDecoration } from './utils/applyAvatarDecoration';
import { applyNameDecorationClass } from './utils/applyNameDecoration';
import { pointsLabel } from '../common/utils/pointsLabel';
import { safeCssUrl } from '../common/utils/safeCssUrl';
/**
 * Lê um atributo booleano do payload do fórum.
 *
 * `app.forum` NÃO existe durante os initializers: `Application.boot()` roda
 * `initialize()` primeiro e só depois atribui `this.forum` a partir da store.
 * Ler `app.forum.attribute` nesse instante estoura
 * `Cannot read properties of undefined` e derruba o initializer inteiro da
 * extensão — nenhuma rota, nenhum componente de notificação, nada. Daí o
 * optional chaining com queda para o fallback.
 */
const settingRaw = (key, fallback) => {
  var _app$forum, _app$forum$attribute;
  const v = (_app$forum = app.forum) == null || (_app$forum$attribute = _app$forum.attribute) == null ? void 0 : _app$forum$attribute.call(_app$forum, key);
  return v === undefined || v === null ? fallback : !!v;
};

/**
 * Toda leitura de configuração passa por aqui, sempre em tempo de RENDER
 * (dentro dos callbacks de `extend` e do `beforeMount`) e nunca em tempo de
 * initializer — ver `settingRaw`.
 *
 * A chave geral (`pointSystem.enabled`) NÃO é consultada aqui de propósito:
 * ela desliga a CONCESSÃO de pontos, não a interface. Saldo, loja, trocas e
 * decorações equipadas continuam à vista para o usuário gastar e usar o que
 * já conquistou.
 */
const setting = function (key, fallback) {
  if (fallback === void 0) {
    fallback = true;
  }
  return settingRaw(key, fallback);
};
app.initializers.add('ramon/point-system', () => {
  // ── Routes ──────────────────────────────────────────────────────────────
  // Tab routes mount the SAME component as the bare route and let it pick
  // the active section from `m.route.param('tab')`. We register the bare
  // path first so `app.route('pointSystem.shop')` resolves to /rewards
  // (used by external nav links) while users that bookmark a specific tab
  // (e.g. /rewards/name) land on it directly.
  app.routes['pointSystem.shop'] = {
    path: '/rewards',
    component: ShopPage
  };
  app.routes['pointSystem.shop.tab'] = {
    path: '/rewards/:tab',
    component: ShopPage
  };
  app.routes['pointSystem.decorations'] = {
    path: '/decorations',
    component: DecorationsPage
  };
  app.routes['pointSystem.decorations.tab'] = {
    path: '/decorations/:tab',
    component: DecorationsPage
  };
  app.routes['pointSystem.trades'] = {
    path: '/trades',
    component: TradesPage
  };
  app.routes['user.trades'] = {
    path: '/u/:username/trades',
    component: UserTradesPage
  };

  // ── Notification components ─────────────────────────────────────────────
  app.notificationComponents.pointsManual = PointsManualNotification;
  app.notificationComponents.pointsEarned = PointsEarnedNotification;
  app.notificationComponents.pointSystemTierClaimed = TierClaimedNotification;
  app.notificationComponents.pointSystemItemGranted = ItemGrantedNotification;
  app.notificationComponents.pointSystemTradeRequested = TradeRequestedNotification;
  app.notificationComponents.pointSystemTradeAccepted = TradeAcceptedNotification;
  app.notificationComponents.pointSystemTradeCompleted = TradeCompletedNotification;

  // ── Inject the dynamic name-decoration <style> block once ───────────────
  // Deferido: `app.forum` NÃO existe durante os initializers — `boot()` roda
  // `initialize()` antes de popular `forum` e `session`, então qualquer
  // leitura de atributo aqui dentro precisa acontecer no `beforeMount`.
  app.beforeMount(() => {
    injectNameDecorationStyles();
    injectTitleDecorationStyles();
    injectPostHighlightDecorationStyles();
    installDomObservers();
    // Hide-badges-with-avatar-deco setting: just toggle a body class. The
    // actual hiding is done by CSS rules in forum.less which use `:has()` to
    // scope to user-containers that wrap a decorated avatar. We deliberately
    // do NOT use a JS DOM hider here — Mithril removes inline styles on
    // every redraw, so styling via CSS is the only reliable path.
    if (setting('pointSystem.hide_badges_with_avatar_deco', false)) {
      document.body.classList.add('ps-hide-badges-with-deco');
    }
  });

  // ── Opt-out por usuário em /settings ────────────────────────────────────
  // Sem uma linha na grade, um tipo de notificação é obrigatório: o usuário
  // recebe e não tem onde desligar. É o que sustenta o padrão "notificar em
  // toda concessão" ser uma opção viável para o admin — quem achar barulho
  // desliga do seu lado.
  //
  // A coluna de e-mail aparece desabilitada, e é o correto: o blueprint
  // registra `alert` e `realtime`, não `email`, então a chave de preferência
  // de e-mail não existe e o core desenha a caixa esmaecida.
  //
  // O alvo vai como STRING, não como classe importada. `NotificationGrid`
  // mora num chunk assíncrono (a página de configurações é code-split), e no
  // instante do initializer `flarum.reg.get` ainda devolve `undefined` — daí
  // `NotificationGrid.prototype` estourar "Cannot read properties of
  // undefined". A forma em string faz o core registrar um `reg.onLoad` e
  // aplicar o extend quando o chunk chegar. É o mesmo caminho que
  // flarum/subscriptions e flarum/mentions usam para esta mesma grade.
  extend('flarum/forum/components/NotificationGrid', 'notificationTypes', function (items) {
    items.add('pointsEarned', {
      name: 'pointsEarned',
      icon: app.forum.attribute('pointSystem.currency_icon') || 'fas fa-coins',
      label: app.translator.trans('ramon-point-system.forum.notifications.earned_preference_label')
    });
  });

  // ── Rewards entry in the IndexSidebar nav dropdown ──────────────────────
  //
  // This is the SAME single hook the avocado theme uses for every nav entry
  // it adds (Popular, Search, Team): a single `extend(IndexSidebar.prototype,
  // 'navItems')` with a priority that slots into the dropdown's natural
  // order (110 → 90 in avocado). We don't add a sibling button on `items()`
  // or stamp the entry on `HeaderPrimary`/`SessionDropdown` — themes that
  // care about mobile (avocado, etc.) already mount IndexSidebar on every
  // page via `PageStructure.sidebar` override, so this dropdown is the
  // canonical place. On stock Flarum, the dropdown is only mounted on the
  // IndexPage — which is the standard core UX; users without a custom theme
  // reach the Rewards page via direct URL or the new-discussion-area links.
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    if (!app.forum.attribute('pointSystemCanViewShop')) return;
    const icon = app.forum.attribute('pointSystem.currency_icon') || 'fas fa-coins';
    items.add('pointSystem-shop', m(LinkButton, {
      href: app.route('pointSystem.shop'),
      icon: icon
    }, app.translator.trans('ramon-point-system.forum.nav.shop')),
    // Sits just below avocado's "Team" (90) but above the default end.
    // Stock Flarum has only "All Discussions" at 100 here, so we land
    // directly under it — a sensible position in either environment.
    85);
  });

  // ── Avatar decoration — applies wherever <Avatar user={...}/> is rendered
  // (covers Flarum core + every theme that uses the standard component).
  extend(Avatar.prototype, 'view', function (vnode) {
    var _user$attribute;
    if (!setting('pointSystem.avatar_deco_enabled')) return;
    const user = this.attrs.user;
    const url = user == null || (_user$attribute = user.attribute) == null ? void 0 : _user$attribute.call(user, 'equippedAvatarDecorationUrl');
    if (!url) return;

    /*
     * "Quadros de avatar em listas de discussões" (toggle adicionado em
     * 2026-05-23): quando o admin desativa, marcamos o vnode pra que
     * o nosso CSS / oncreate suprima o frame se ele cair dentro de
     * `.DiscussionListItem`. Mithril não tem contexto de árvore
     * disponível no `view` extender, então passamos a decisão como
     * uma flag no vnode e finalizamos no DOM via `oncreate`.
     */
    const inListsAllowed = setting('pointSystem.avatar_deco_in_lists');
    applyAvatarDecoration(vnode, resolveAssetUrl(url), {
      skipInDiscussionList: !inListsAllowed
    });
  });

  // ── Name decoration — append `ps-name-{slug}` to the rendered root via
  // each component's `classes()` array method. This is the idiomatic Flarum 2
  // path: classes() runs BEFORE the className string is joined into attrs,
  // so the class participates in the normal flow instead of being mutated
  // onto an already-built vnode (which is brittle for class components).
  extend(CommentPost.prototype, 'classes', function (classes) {
    var _this$attrs$post, _this$attrs$post$user;
    const user = (_this$attrs$post = this.attrs.post) == null || (_this$attrs$post$user = _this$attrs$post.user) == null ? void 0 : _this$attrs$post$user.call(_this$attrs$post);
    if (setting('pointSystem.name_deco_enabled') && setting('pointSystem.deco_in_posts')) {
      pushDecoClass(classes, user);
    }
    // Post highlight: tag the CommentPost root with `ps-posthl-{slug}` so the
    // global stylesheet (built-in presets + admin custom CSS injected above)
    // can render a border / glow / ribbon around the post. Same plumbing as
    // the name decoration class push — relies on the Mithril classes() hook
    // (no DOM mutation) so it survives every redraw.
    if (setting('pointSystem.post_hl_deco_enabled') && setting('pointSystem.deco_in_posts')) {
      pushPostHlClass(classes, user);
    }
  });
  extend(UserCard.prototype, 'view', function (vnode) {
    // UserCard doesn't have a classes() method, so fall back to vnode mutation.
    if (setting('pointSystem.name_deco_enabled') && setting('pointSystem.deco_in_user_card')) {
      applyNameDecorationClass(vnode, this.attrs.user);
    }

    // Cover decoration: stamp a CSS class + custom property on the root so
    // the LESS rule below can render the banner as `::before`. We avoid
    // setting `background-image` directly so the cover sits in its own
    // stacking context (clean rounded corners, no overlay bleed on text).
    if (setting('pointSystem.cover_deco_enabled')) {
      var _user$attribute2;
      const user = this.attrs.user;
      const coverPath = user == null || (_user$attribute2 = user.attribute) == null ? void 0 : _user$attribute2.call(user, 'equippedCoverDecorationUrl');
      if (coverPath) {
        const url = resolveAssetUrl(String(coverPath));
        vnode.attrs = vnode.attrs || {};
        const existing = String(vnode.attrs.className || '');
        if (!existing.includes('ps-has-cover')) {
          vnode.attrs.className = "".concat(existing, " ps-has-cover").trim();
        }
        const prevStyle = vnode.attrs.style || '';
        const safeUrl = safeCssUrl(url);
        const styleAdd = "--ps-cover-url: url(\"".concat(safeUrl, "\");");
        vnode.attrs.style = typeof prevStyle === 'string' ? "".concat(prevStyle).concat(prevStyle.endsWith(';') || !prevStyle ? '' : ';', " ").concat(styleAdd) : _objectSpread(_objectSpread({}, prevStyle), {}, {
          '--ps-cover-url': "url(\"".concat(safeUrl, "\")")
        });
      }
    }
  });
  extend(DiscussionListItem.prototype, 'elementAttrs', function (attrs) {
    var _this$attrs$discussio, _this$attrs$discussio2, _this$attrs$discussio3;
    if (!setting('pointSystem.name_deco_enabled') || !setting('pointSystem.deco_in_lists')) return;
    const slug = (_this$attrs$discussio = this.attrs.discussion) == null || (_this$attrs$discussio2 = _this$attrs$discussio.user) == null || (_this$attrs$discussio2 = _this$attrs$discussio2.call(_this$attrs$discussio)) == null || (_this$attrs$discussio3 = _this$attrs$discussio2.attribute) == null ? void 0 : _this$attrs$discussio3.call(_this$attrs$discussio2, 'equippedNameDecorationSlug');
    if (!slug) return;
    const cleanSlug = String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
    if (!cleanSlug) return;
    attrs.className = (attrs.className || '') + ' ps-name-' + cleanSlug;
  });

  // ── "Award points" entry in the user-profile Controls dropdown ──────────
  // Visible only when the actor has `pointSystem.manage`. Opens a small modal
  // that posts to the existing /api/point-system/award endpoint (which also
  // enforces the same permission server-side).
  extend(UserControls, 'moderationControls', function (items, user) {
    if (!app.session.user) return;
    if (!app.forum.attribute('pointSystemCanManage')) return;
    items.add('pointSystem-award', m(Button, {
      icon: "fas fa-coins",
      onclick: () => app.modal.show(AwardPointsModal, {
        user
      })
    }, app.translator.trans('ramon-point-system.forum.user_controls.award_points')), 85);
  });

  // ── "Trade" entry in the user-profile Controls dropdown ─────────────────
  // Gated by:
  //   - the trade master toggle (`pointSystemTradeEnabled`)
  //   - the per-actor permission (`pointSystemCanTrade`, which is
  //     `pointSystem.trade` AND the master toggle on the server side)
  //   - not the actor's own profile (you cannot trade with yourself)
  //
  // Extension point is `userControls` — Flarum core's UserControls.js defines
  // exactly three ItemList groups: `userControls` (regular user actions),
  // `moderationControls` (admin), `destructiveControls` (delete). The earlier
  // `userActions` name was wrong — that group doesn't exist in core, so the
  // button never rendered.
  // Flarum's typing of `extend()` declares one callback argument, but the
  // userControls hook actually receives `(items, user)` at runtime; cast to
  // bypass the typing mismatch.
  extend(UserControls, 'userControls', function (items, user) {
    var _me$id, _user$id;
    const me = app.session.user;
    if (!me) return;
    if (Number((_me$id = me.id) == null ? void 0 : _me$id.call(me)) === Number(user == null || (_user$id = user.id) == null ? void 0 : _user$id.call(user))) return;
    if (!app.forum.attribute('pointSystemTradeEnabled')) return;
    if (!app.forum.attribute('pointSystemCanTrade')) return;
    items.add('pointSystem-trade', m(Button, {
      icon: "fas fa-handshake",
      onclick: () => app.modal.show(TradeModal, {
        target: user
      })
    }, app.translator.trans('ramon-point-system.forum.user_controls.trade')), 80);
  });

  // ── "Trades" tab on the user profile sidebar — self-only ──────────────
  // Only visible when the viewer IS the profile owner. The corresponding
  // UserTradesPage component does its own self-check on mount as a hard
  // gate; this extension just hides the nav entry to other visitors so
  // they don't see the link at all.
  extend(UserPage.prototype, 'navItems', function (items) {
    var _me$id2, _user$id2;
    const user = this.user;
    if (!user) return;
    const me = app.session.user;
    if (!me || Number((_me$id2 = me.id) == null ? void 0 : _me$id2.call(me)) !== Number((_user$id2 = user.id) == null ? void 0 : _user$id2.call(user))) return;
    if (!app.forum.attribute('pointSystemTradeEnabled')) return;

    // Use the top-level `import LinkButton from 'flarum/common/components/LinkButton'`.
    // The earlier `require(...).default` shim returned undefined under the
    // bundler's external-module rewrite (Flarum 2's webpack maps `flarum/*`
    // strings to externals, and the `.default` accessor fired against an
    // undefined module → Mithril's "selector must be a string or component"
    // crash on the user profile route.
    items.add('pointSystem-trades', m(LinkButton, {
      href: app.route('user.trades', {
        username: user.slug()
      }),
      icon: "fas fa-handshake"
    }, app.translator.trans('ramon-point-system.forum.user_profile.trades_link')), 85);
  });

  // ── Session dropdown entries (next to "Settings" / "Profile") ──────────
  // Adds "My decorations" — always visible — and "Trades" — only when
  // the master toggle is on AND the actor has `pointSystem.trade`.
  // Priorities slot above the core `settings: 50` entry so the order is:
  // Profile (100) → My decorations (80) → Trades (75) → Settings (50).
  extend(SessionDropdown.prototype, 'items', function (items) {
    if (!app.session.user) return;
    items.add('pointSystem-decorations', m(LinkButton, {
      icon: "fas fa-tshirt",
      href: app.route('pointSystem.decorations')
    }, app.translator.trans('ramon-point-system.forum.session_dropdown.my_decorations')), 80);
    if (app.forum.attribute('pointSystemTradeEnabled') && app.forum.attribute('pointSystemCanTrade')) {
      items.add('pointSystem-trades', m(LinkButton, {
        icon: "fas fa-handshake",
        href: app.route('pointSystem.trades')
      }, app.translator.trans('ramon-point-system.forum.session_dropdown.trades')), 75);
    }
  });

  // ── Points badge + custom title in the post header ─────────────────────
  // The title is rendered in BOTH `.Post-side` (sideItems below) AND here in
  // `.PostUser` so the post can show it whichever side column is visible at
  // the current breakpoint. CSS in less/forum.less hides whichever copy
  // doesn't belong to the active layout — Post-side on @phone, PostUser on
  // @tablet-up — so only one is ever visible at a time.
  extend(PostUser.prototype, 'userViewItems', function (items) {
    var _this$attrs$post2, _this$attrs$post2$use;
    const user = (_this$attrs$post2 = this.attrs.post) == null || (_this$attrs$post2$use = _this$attrs$post2.user) == null ? void 0 : _this$attrs$post2$use.call(_this$attrs$post2);
    if (!user) return;
    if (setting('pointSystem.show_in_post_header')) {
      items.add('pointSystem-postBadge', pointsBadge(user), 85);
    }
    if (setting('pointSystem.title_deco_enabled') && setting('pointSystem.deco_in_posts')) {
      const node = userTitleBadge(user, 'PointSystemUserTitle--inHeader');
      if (node) items.add('pointSystem-userTitle', node, 40);
    }
  });

  // ── Custom title below the avatar in the post side column ──────────────
  // CommentPost.sideItems is the canonical hook for things that sit next to
  // the avatar in `.Post-side`. Works on both Flarum core (avatar-only column)
  // and Avocado (which already overrides this ItemList to add `.Post-side-inner`).
  // Adding with priority 50 places the title after the avatar(=100) in both.
  extend(CommentPost.prototype, 'sideItems', function (items) {
    var _this$attrs$post3, _this$attrs$post3$use;
    if (!setting('pointSystem.title_deco_enabled') || !setting('pointSystem.deco_in_posts')) return;
    const user = (_this$attrs$post3 = this.attrs.post) == null || (_this$attrs$post3$use = _this$attrs$post3.user) == null ? void 0 : _this$attrs$post3$use.call(_this$attrs$post3);
    if (!user) return;
    const node = userTitleBadge(user, 'PointSystemUserTitle--inSide');
    if (node) items.add('pointSystem-userTitle', node, 50);
  });

  // ── Points badge + equipped title on the user profile card ────────────
  // Adding the title here (same renderer as post-header / sidebar) so the
  // user's equipped title actually shows up on /u/username. Gated by both
  // the global "show points on profile" toggle AND the title-deco feature
  // flag so a forum that has titles disabled doesn't leak the markup.
  extend(UserCard.prototype, 'infoItems', function (items) {
    const user = this.attrs.user;
    if (!user) return;
    if (setting('pointSystem.show_in_user_profile')) {
      items.add('pointSystem-profileBadge', pointsBadge(user, 'PointSystemPostBadge--inProfile'), 50);
    }
    if (setting('pointSystem.title_deco_enabled')) {
      const node = userTitleBadge(user, 'PointSystemUserTitle--inProfile');
      if (node) items.add('pointSystem-userTitle', node, 48);
    }
  });
});

/**
 * Pílula de saldo. `variantClass` distingue o contexto de render: sem
 * modificador a pílula vive no cabeçalho do post (escondida no phone, onde
 * a linha do byline não tem folga); com `--inProfile` ela vive no cartão de
 * perfil, onde precisa sobreviver ao breakpoint mobile e usar o âmbar da
 * moeda em vez do tom primário do tema, que some sobre heros coloridos.
 */
function pointsBadge(user, variantClass) {
  var _user$attribute3;
  if (variantClass === void 0) {
    variantClass = '';
  }
  /*
   * `pointBalance` só é serializado para quem pode ver o saldo daquele
   * usuário — dono, gestor, ou grupo com `pointSystem.viewOthers`
   * (ver UserFields::canSeePoints). Para os demais o atributo vem AUSENTE.
   *
   * O `?? 0` de antes transformava essa ausência em "0 pontos" e a pílula
   * anunciava zero para todo mundo, inclusive em perfis de usuários com
   * saldo alto — informação errada, não apenas faltante. Sem permissão a
   * pílula não é desenhada.
   */
  const raw = (_user$attribute3 = user.attribute) == null ? void 0 : _user$attribute3.call(user, 'pointBalance');
  if (raw === undefined || raw === null) return null;
  const balance = Number(raw);
  if (!Number.isFinite(balance)) return null;
  const icon = app.forum.attribute('pointSystem.currency_icon') || 'fas fa-coins';
  const cls = ['PointSystemPostBadge', variantClass].filter(Boolean).join(' ');
  return m("span", {
    className: cls,
    title: balance.toLocaleString() + ' ' + pointsLabel(app)
  }, m("i", {
    className: icon,
    "aria-hidden": "true"
  }), " ", balance.toLocaleString());
}
function userTitleBadge(user, variantClass) {
  var _user$attribute4, _user$attribute5;
  if (variantClass === void 0) {
    variantClass = '';
  }
  const slug = user == null || (_user$attribute4 = user.attribute) == null ? void 0 : _user$attribute4.call(user, 'equippedTitleDecorationSlug');
  const text = user == null || (_user$attribute5 = user.attribute) == null ? void 0 : _user$attribute5.call(user, 'equippedTitleDecorationText');
  if (!slug || !text) return null;
  const cleanSlug = String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
  if (!cleanSlug) return null;
  const cls = ['PointSystemUserTitle', "ps-title-".concat(cleanSlug), variantClass].filter(Boolean).join(' ');
  return m("span", {
    className: cls
  }, text);
}

// ─── Helpers ──────────────────────────────────────────────────────────────
// Name-decoration CSS supports two modes:
//   1. Property list (`color: red; font-weight: bold;`) — wrapped in the
//      default selector chain. Legacy / quick decorations.
//   2. Full CSS (contains `{`) — injected as-is, with `&` replaced by the
//      selector chain. Supports `@keyframes`, pseudo-elements, multi-rule.
// Inject CSS for title-decorations at boot. Same approach as name decorations:
// the admin-authored CSS is wrapped in a stable selector chain and a `<style>`
// block is appended to the head. Custom rules support both modes (property
// list / full CSS with `&` placeholder) and are sanitised server-side.
function injectTitleDecorationStyles() {
  var _document$getElementB;
  const id = 'ps-title-deco-runtime';
  (_document$getElementB = document.getElementById(id)) == null || _document$getElementB.remove();
  const decos = app.forum.attribute('pointSystemTitleDecorations') || [];
  if (!Array.isArray(decos) || decos.length === 0) return;
  const out = [];
  for (const d of decos) {
    if (!d.slug) continue;
    const cls = cssClass(d.slug);
    if (!cls) continue;
    const sel = ".ps-title-".concat(cls, ",.ps-title-preview.ps-title-").concat(cls);

    // Always set the colour variable so presets/css can reference it.
    if (d.color) {
      out.push("".concat(sel, " { --ps-title-color: ").concat(String(d.color).replace(/[<>"';]/g, ''), "; }"));
    }
    const css = String(d.customCss || '').trim();
    if (!css) continue;
    if (css.includes('{')) {
      out.push(bangify(css.replace(/&/g, sel)));
    } else {
      out.push("".concat(sel, " { ").concat(bangify(css.replace(/}/g, '} ')), " }"));
    }
  }
  if (out.length === 0) return;
  appendStyle(id, out.join('\n'));
}
function injectPostHighlightDecorationStyles() {
  var _document$getElementB2;
  const id = 'ps-posthl-deco-runtime';
  (_document$getElementB2 = document.getElementById(id)) == null || _document$getElementB2.remove();
  const decos = app.forum.attribute('pointSystemPostHighlightDecorations') || [];
  if (!Array.isArray(decos) || decos.length === 0) return;
  const out = [];
  for (const d of decos) {
    if (!d.slug || !d.customCss) continue;
    const cls = cssClass(d.slug);
    if (!cls) continue;
    // Highlight applies to the whole post container (CommentPost) and to the
    // shop card preview. We expose both selectors to the user's `&` symbol.
    const sel = ".CommentPost.ps-posthl-".concat(cls, ",") + ".Post.ps-posthl-".concat(cls, ",") + ".ps-posthl-preview.ps-posthl-".concat(cls);
    const css = String(d.customCss).trim();
    if (css.includes('{')) {
      out.push(bangify(css.replace(/&/g, sel)));
    } else {
      out.push("".concat(sel, " { ").concat(bangify(css.replace(/}/g, '} ')), " }"));
    }
  }
  if (out.length === 0) return;
  appendStyle(id, out.join('\n'));
}
function injectNameDecorationStyles() {
  var _document$getElementB3;
  const id = 'ps-name-deco-runtime';
  (_document$getElementB3 = document.getElementById(id)) == null || _document$getElementB3.remove();
  const decos = app.forum.attribute('pointSystemNameDecorations') || [];
  if (!Array.isArray(decos) || decos.length === 0) return;
  const out = [];
  for (const d of decos) {
    if (!d.slug || !d.customCss) continue;
    const cls = cssClass(d.slug);
    if (!cls) continue;

    // Decoration target shapes (all are text-only username carriers — never
    // the wrapper, avatar or badges). User-authored custom CSS for these
    // selectors should win the cascade against theme rules, so we append
    // `!important` to every property of the raw input below.
    //
    // The descendant selector `.ps-name-${cls} .username` would normally
    // also match usernames of OTHER users rendered inside the post —
    // notably `.Post-likedBy .username` ("Ramon liked this") and quoted
    // content. Exclude those via `:not(.Post-likedBy *)` etc. so the
    // current actor's name decoration doesn't bleed onto unrelated
    // usernames embedded in the post body. `.Post-mentionedBy-preview`
    // e `.PostPreview` são listados separadamente porque o preview é
    // ANEXADO como IRMÃO do `.Post-mentionedBy` (não descendente) por
    // flarum/mentions, então `:not(.Post-mentionedBy *)` não cobre.
    const inDescendant = ".ps-name-".concat(cls, " .username:not(.Post-likedBy *):not(.Post-mentionedBy *):not(.Post-mentionedBy-preview *):not(.PostPreview *):not(blockquote *):not(.UserMention *)");
    const selectors = ".ps-name-preview.ps-name-".concat(cls, ",") + "".concat(inDescendant, ",") + ".username.ps-name-".concat(cls, ",") + "a.ps-name-".concat(cls);
    const css = String(d.customCss).trim();
    if (css.includes('{')) {
      out.push(bangify(css.replace(/&/g, selectors)));
    } else {
      out.push("".concat(selectors, " { ").concat(bangify(css.replace(/}/g, '} ')), " }"));
    }
  }
  if (out.length === 0) return;
  appendStyle(id, out.join('\n'));
}

// Append `!important` to every property declaration so user-authored
// decorations win the cascade against theme rules (avocado, etc.).
// Skip declarations that already include `!important`.
function bangify(s) {
  return s.replace(/([\w\-]+\s*:\s*[^;{}]+?)(\s*;)/g, (m, decl, semi) => /!\s*important/i.test(decl) ? m : decl + ' !important' + semi);
}
function appendStyle(id, textContent) {
  const style = document.createElement('style');
  style.id = id;
  style.textContent = textContent;
  document.head.appendChild(style);
}

// ─── DOM observation (single MutationObserver) ────────────────────────────
// All theme-level taggers we install (per-letter rewriter, theme username
// tagger, `.username` span tagger, Avocado profile hooks) need to react to
// the same DOM mutations. We collect their per-node and per-attribute
// callbacks once and run all of them against each batched mutation list,
// so the page only pays for ONE `document.body` subtree observer instead of
// the four we used to install side-by-side. The cost was visible on long
// discussion pages where every Mithril redraw fired four parallel scans.

const PER_LETTER_SLUGS = new Set(['wave']);
const addedHandlers = [];
const attributeHandlers = [];
function onAdded(selector, run) {
  addedHandlers.push({
    selector,
    run,
    scan: root => {
      var _root$querySelectorAl;
      return (_root$querySelectorAl = root.querySelectorAll) == null ? void 0 : _root$querySelectorAl.call(root, selector).forEach(run);
    }
  });
}
function onAttributeChange(handler) {
  attributeHandlers.push(handler);
}
function installDomObservers() {
  if (window.__psObserverInstalled) return;
  window.__psObserverInstalled = true;
  registerPerLetterRewriter();
  registerThemeUsernameTagger();
  registerUsernameSpanTagger();
  registerAvocadoProfileHooks();

  // Initial scan of the document for handlers that registered above.
  for (const h of addedHandlers) h.scan(document);
  const hasAttributeHandlers = attributeHandlers.length > 0;
  new MutationObserver(muts => {
    for (const mu of muts) {
      if (mu.type === 'attributes' && mu.attributeName) {
        const target = mu.target;
        for (const ah of attributeHandlers) ah(target, mu.attributeName);
        continue;
      }
      mu.addedNodes.forEach(node => {
        if (!(node instanceof Element)) return;
        for (const h of addedHandlers) {
          var _node$matches;
          if ((_node$matches = node.matches) != null && _node$matches.call(node, h.selector)) h.run(node);else h.scan(node);
        }
      });
    }
  }).observe(document.body, _objectSpread({
    childList: true,
    subtree: true
  }, hasAttributeHandlers ? {
    attributes: true,
    attributeFilter: ['class']
  } : {}));
}

// ─── Per-letter rewriter ─────────────────────────────────────────────────
// Some presets (Wave) require each character of the username to be its own
// `<span>` so they can be animated with staggered `animation-delay`s. CSS
// can't do that on its own, so we walk new `.username` elements at runtime
// and rewrite their text into per-character spans — but ONLY when their
// closest `ps-name-{slug}` ancestor matches a slug in PER_LETTER_SLUGS.
function registerPerLetterRewriter() {
  // Find the active deco slug. Look at the element's OWN classes first
  // (covers `.ps-name-preview.ps-name-X` used by the shop/admin live preview),
  // then walk up to find an ancestor wrapper class (`.ps-name-X` on
  // CommentPost, UserCard, etc., with `.username` inside).
  const findDecoSlug = el => {
    let node = el;
    while (node) {
      for (const cls of Array.from(node.classList)) {
        if (cls.startsWith('ps-name-') && cls !== 'ps-name-preview') {
          return cls.slice('ps-name-'.length);
        }
      }
      node = node.parentElement;
    }
    return null;
  };
  const rewrite = el => {
    const slug = findDecoSlug(el);
    if (!slug || !PER_LETTER_SLUGS.has(slug)) return;
    if (el.dataset.psPerLetter === slug) return;
    el.dataset.psPerLetter = slug;

    // Snapshot childNodes — we mutate during iteration. Only text nodes get
    // replaced with per-letter spans. Other children (like the verified
    // popover anchor inside `.AvocadoUserPage-hero-name`) stay intact.
    //
    // Each generated span gets `.ps-letter` so CSS rules can target them
    // unambiguously, AND a `--ps-i` custom property carrying the letter index
    // so the animation-delay can be computed via `calc(var(--ps-i) * 0.05s)`
    // — works regardless of how many non-letter siblings are around (verified
    // badge, etc.) and scales to any name length without an nth-child list.
    const nodes = Array.from(el.childNodes);
    let index = 0;
    for (const node of nodes) {
      if (node.nodeType !== Node.TEXT_NODE) continue;
      const text = (node.textContent || '').trim();
      if (!text) continue;
      const frag = document.createDocumentFragment();
      for (const ch of text) {
        const span = document.createElement('span');
        span.className = 'ps-letter';
        span.style.setProperty('--ps-i', String(index));
        span.textContent = ch;
        frag.appendChild(span);
        index++;
      }
      node.replaceWith(frag);
    }
  };

  // Scan targets:
  //   - `.username`             Flarum's username helper
  //   - `.ps-name-preview`      shop/admin live preview
  //   - `.ps-name-text`         our wrapper span for theme contexts that mix
  //                             username text with sibling badges (avocado h1)
  //   - `a[data-ps-name-deco]`  anchors tagged by registerThemeUsernameTagger
  //                             (avocado thread cards, mentions, etc.)
  onAdded('.username, .ps-name-preview, .ps-name-text, a[data-ps-name-deco]', rewrite);
}

// ─── Theme username decoration tagger ───────────────────────────────────
// For themes (Avocado, etc.) that render a username as plain text inside an
// anchor link (`<a class="AvocadoHome-threadAuthor" href="/u/X">Ramon</a>`)
// instead of using Flarum's `username()` helper that emits `.username`. Our
// regular CSS selectors don't match that markup, so we tag the anchor itself
// at runtime with `ps-name-{slug}`. Critically, we SKIP anchors that wrap an
// `<img>` / `.Avatar` — those are avatar links, NOT name links, and tagging
// them would apply the decoration to the avatar.
// Containers where username links appear in NON-authorial contexts —
// "X liked this", "mentioned by X", quote buttons that reference a user,
// etc. Decorating these makes the deco bleed into prose ("Ramon liked
// this" gets the glitch animation on just "Ramon" amid surrounding plain
// text), which the user reported as a bug. The tagger skips any anchor /
// span sitting inside one of these.
const SECONDARY_USERNAME_CONTAINERS = ['.Post-likedBy', '.Post-mentionedBy',
// O dropdown de preview do `Post-mentionedBy` é anexado como IRMÃO
// (não descendente) do `.Post-mentionedBy` via `this.$().append(...)`
// em flarum/mentions/.../addMentionedByList.js — `.closest(
// '.Post-mentionedBy')` aqui NÃO pega ele. Lista explícita.
'.Post-mentionedBy-preview',
// `.PostPreview` é o card que aparece dentro do dropdown acima e
// também em MentionedByModal / SearchResult — é prosa, não cabeçalho
// autoral. Decorar o nome aqui sangra para fora do contexto autoral.
'.PostPreview', '.PostMention', '.Post-quoteButtonContainer', '.Post-actions', '.Notification', '.NotificationList', '.SearchResult', '.DiscussionListItem-info'].join(',');
function isInSecondaryContext(el) {
  return !!el.closest(SECONDARY_USERNAME_CONTAINERS);
}
function registerThemeUsernameTagger() {
  if (!setting('pointSystem.name_deco_enabled') || !setting('pointSystem.deco_in_lists')) return;
  const decorate = el => {
    var _user$displayName, _user$displayName2, _user$username, _user$username2, _user$attribute6;
    const a = el;
    if (a.dataset.psNameDeco) return;
    if (isInSecondaryContext(a)) return;
    // Skip avatar-wrapping anchors — the username link is text-only.
    if (a.querySelector('img, .Avatar, .ps-avatar-deco-wrap')) return;
    const m = /\/u\/([^/?#]+)/.exec(a.getAttribute('href') || '');
    if (!m) return;
    a.dataset.psNameDeco = '1';
    const username = decodeURIComponent(m[1]).toLowerCase();
    const users = app.store.all('users');
    const user = users.find(u => {
      var _u$username, _u$username2;
      return String((_u$username = (_u$username2 = u.username) == null ? void 0 : _u$username2.call(u)) != null ? _u$username : '').toLowerCase() === username;
    });
    if (!user) return;

    // Only decorate if the anchor's visible text actually IS the user's name.
    // This rules out generic-link cases like "You like this", "Reply", or any
    // sentence where the link target happens to be the user's profile but the
    // text is something else entirely. Allows the `@` mention prefix.
    const text = (a.textContent || '').trim().toLowerCase().replace(/^@\s*/, '');
    const dn = String((_user$displayName = (_user$displayName2 = user.displayName) == null ? void 0 : _user$displayName2.call(user)) != null ? _user$displayName : '').trim().toLowerCase();
    const un = String((_user$username = (_user$username2 = user.username) == null ? void 0 : _user$username2.call(user)) != null ? _user$username : '').trim().toLowerCase();
    const isName = text === dn || text === un ||
    // Allow trailing icons/badges/etc. that get textContent-concatenated.
    dn && text.startsWith(dn) || un && text.startsWith(un);
    if (!isName) return;
    const slug = (_user$attribute6 = user.attribute) == null ? void 0 : _user$attribute6.call(user, 'equippedNameDecorationSlug');
    if (!slug) return;
    const safe = String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
    if (safe) a.classList.add("ps-name-".concat(safe));
  };
  onAdded('a[href*="/u/"]', decorate);
}

// ─── Universal `.username` tagger ────────────────────────────────────────
// Flarum's `username()` helper emits `<span class="username">{name}</span>`
// wherever a user's display name is rendered (post header, mentions, user
// list, etc.) and the span has no inherent user identifier. We find each
// `.username`, walk up to its closest `<a href="/u/{username}">`, look the
// user up in the store, and add `ps-name-{slug}` directly to the span.
//
// This is a defense-in-depth layer: even if a host theme overrides post
// rendering or our `CommentPost.classes()` extension misses an early render
// (subtree.check skipping diff before the user model loads), this scanner
// still applies the decoration once the DOM is in place.
function registerUsernameSpanTagger() {
  if (!setting('pointSystem.name_deco_enabled')) return;
  const decorate = span => {
    var _user$attribute7;
    if (span.dataset.psNameDeco === '1') return;
    if (isInSecondaryContext(span)) return;

    // Find the closest ancestor anchor pointing at `/u/{username}`.
    let node = span;
    let anchor = null;
    while (node && !anchor) {
      const parent = node.parentElement;
      if (!parent) break;
      if (parent.tagName === 'A' && /\/u\//.test(parent.getAttribute('href') || '')) {
        anchor = parent;
      }
      node = parent;
    }
    if (!anchor) return;
    const m = /\/u\/([^/?#]+)/.exec(anchor.getAttribute('href') || '');
    if (!m) return;
    const username = decodeURIComponent(m[1]).toLowerCase();
    const users = app.store.all('users');
    const user = users.find(u => {
      var _u$username3, _u$username4;
      return String((_u$username3 = (_u$username4 = u.username) == null ? void 0 : _u$username4.call(u)) != null ? _u$username3 : '').toLowerCase() === username;
    });
    if (!user) return;
    const slug = (_user$attribute7 = user.attribute) == null ? void 0 : _user$attribute7.call(user, 'equippedNameDecorationSlug');
    if (!slug) return;
    const safe = String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
    if (!safe) return;
    span.dataset.psNameDeco = '1';
    // Also stash the slug so the attribute-observer below can re-apply the
    // class after Mithril rewrites the className during redraws.
    span.dataset.psSlug = safe;
    span.classList.add('ps-name-' + safe);
  };
  onAdded('.username', decorate);

  // Re-apply the class whenever Mithril rewrites the className on a tagged
  // `.username`. The vnode for `username()` is just `<span class="username">`,
  // so any redraw of the post stream resets the class to just "username" and
  // strips our `ps-name-X`. Watch for that and put it back.
  onAttributeChange((target, name) => {
    var _el$dataset, _el$dataset2;
    if (name !== 'class') return;
    const el = target;
    if (((_el$dataset = el.dataset) == null ? void 0 : _el$dataset.psNameDeco) === '1' && (_el$dataset2 = el.dataset) != null && _el$dataset2.psSlug && !el.classList.contains('ps-name-' + el.dataset.psSlug)) {
      el.classList.add('ps-name-' + el.dataset.psSlug);
    }
  });

  // When the user model lands AFTER its `.username` DOM mounted, the
  // observer doesn't re-fire (the span itself didn't mutate). Two delayed
  // re-scans cover that window without the previous 10× polling churn.
  setTimeout(() => document.querySelectorAll('.username').forEach(decorate), 400);
  setTimeout(() => document.querySelectorAll('.username').forEach(decorate), 1600);
}

// ─── Avocado theme: profile hero hooks ───────────────────────────────────
// Avocado renders its own profile layout (`AvocadoUserPage-hero`) instead of
// using Flarum's UserCard, so our component extensions don't fire here.
// Two DOM-level hooks:
//   1. Inject a points pill into `.AvocadoUserPage-hero-stats`.
//   2. Tag `.AvocadoUserPage-hero-name` (the H1 with the displayName) with
//      `ps-name-{slug}` so the user's equipped name decoration applies.
function registerAvocadoProfileHooks() {
  const showPoints = setting('pointSystem.show_in_user_profile');
  const showName = setting('pointSystem.name_deco_enabled') && setting('pointSystem.deco_in_user_card');
  const showCover = setting('pointSystem.cover_deco_enabled');
  const showTitle = setting('pointSystem.title_deco_enabled') && setting('pointSystem.deco_in_user_card');
  if (!showPoints && !showName && !showCover && !showTitle) return;
  const resolveUser = () => {
    const match = /\/u\/([^/?#]+)/.exec(window.location.pathname);
    if (!match) return null;
    const username = decodeURIComponent(match[1]).toLowerCase();
    const users = app.store.all('users');
    return users.find(u => {
      var _u$username5, _u$username6;
      return String((_u$username5 = (_u$username6 = u.username) == null ? void 0 : _u$username6.call(u)) != null ? _u$username5 : '').toLowerCase() === username;
    }) || null;
  };
  const injectPoints = el => {
    var _user$attribute8;
    const statsEl = el;
    if (!showPoints || statsEl.dataset.psPoints === '1') return;
    const user = resolveUser();
    if (!user) return;

    // Mesmo contrato de `pointsBadge`: atributo ausente = sem permissão de
    // ver o saldo, e aí não se desenha pílula nenhuma. Note que o marcador
    // `psPoints` só é gravado DEPOIS desta checagem — se a permissão ainda
    // não chegou (payload do usuário carregando), uma passada posterior do
    // observador ainda pode injetar.
    const rawBalance = (_user$attribute8 = user.attribute) == null ? void 0 : _user$attribute8.call(user, 'pointBalance');
    if (rawBalance === undefined || rawBalance === null) return;
    statsEl.dataset.psPoints = '1';
    // `AvocadoUserPage-metaItem` faz a pílula herdar a tipografia e o
    // espaçamento dos outros itens da linha ("Membro desde…", "visto há…").
    // Sem ela o saldo aparecia com peso e cor próprios no meio de uma fila
    // que o tema desenha uniforme.
    const balance = Number(rawBalance);
    const rawIcon = app.forum.attribute('pointSystem.currency_icon') || 'fas fa-coins';
    // Strict allowlist on the icon class — only Font Awesome-style tokens.
    const safeIcon = /^[a-zA-Z0-9 _-]{1,80}$/.test(rawIcon) ? rawIcon : 'fas fa-coins';
    const pill = document.createElement('span');
    pill.className = 'AvocadoUserPage-metaItem PointSystemProfilePill';
    const iconEl = document.createElement('i');
    iconEl.className = safeIcon;
    iconEl.setAttribute('aria-hidden', 'true');
    pill.appendChild(iconEl);
    const strong = document.createElement('strong');
    strong.textContent = balance.toLocaleString();
    pill.appendChild(strong);
    pill.appendChild(document.createTextNode(' ' + pointsLabel(app)));
    statsEl.appendChild(pill);
  };
  const injectCover = el => {
    var _user$attribute9;
    if (!showCover) return;
    const heroEl = el;
    if (heroEl.dataset.psCover === '1') return;
    const user = resolveUser();
    if (!user) return;
    const coverPath = (_user$attribute9 = user.attribute) == null ? void 0 : _user$attribute9.call(user, 'equippedCoverDecorationUrl');
    if (!coverPath) return;
    const url = resolveAssetUrl(String(coverPath));
    heroEl.dataset.psCover = '1';
    heroEl.classList.add('ps-has-cover');
    heroEl.style.setProperty('--ps-cover-url', "url(\"".concat(safeCssUrl(url), "\")"));
  };
  const tagName = el => {
    var _user$attribute0;
    if (!showName) return;
    const nameEl = el;
    if (nameEl.dataset.psNameDeco === '1') return;
    const user = resolveUser();
    if (!user) return;
    const slug = (_user$attribute0 = user.attribute) == null ? void 0 : _user$attribute0.call(user, 'equippedNameDecorationSlug');
    if (!slug) return;
    const cleanSlug = String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
    if (!cleanSlug) return;

    // Wrap ONLY the first text node ("Ramon") in our decoration span, leaving
    // the `<span class="VerifiedPopover-anchor">` (and any other sibling
    // element) untouched. If we tagged the h1 directly, CSS color/animation
    // would inherit into the popover, painting the tooltip text too.
    let textNode = null;
    for (const child of Array.from(nameEl.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE && (child.textContent || '').trim()) {
        textNode = child;
        break;
      }
    }
    if (!textNode) return;
    nameEl.dataset.psNameDeco = '1';
    const wrapper = document.createElement('span');
    wrapper.className = 'ps-name-text ps-name-' + cleanSlug;
    wrapper.textContent = textNode.textContent;
    textNode.replaceWith(wrapper);
  };

  // Inject the equipped-title chip inline next to the username in Avocado's
  // hero. Our `UserCard.infoItems` extension renders the chip on default
  // Flarum, but Avocado replaces the UserCard with its own hero markup —
  // the infoItems hook still fires (Avocado extends UserCard) but its
  // chip ends up inside a list that Avocado visually relocates, so the
  // chip never lands next to the name. We attach DIRECTLY to the
  // `.AvocadoUserPage-hero-name` H1 instead.
  const injectTitle = el => {
    var _user$attribute1, _user$attribute10;
    if (!showTitle) return;
    const nameEl = el;
    if (nameEl.dataset.psTitleChip === '1') return;
    const user = resolveUser();
    if (!user) return;
    const slug = (_user$attribute1 = user.attribute) == null ? void 0 : _user$attribute1.call(user, 'equippedTitleDecorationSlug');
    const text = (_user$attribute10 = user.attribute) == null ? void 0 : _user$attribute10.call(user, 'equippedTitleDecorationText');
    if (!slug || !text) return;
    const cleanSlug = String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
    if (!cleanSlug) return;
    nameEl.dataset.psTitleChip = '1';
    const chip = document.createElement('span');
    chip.className = 'PointSystemUserTitle ps-title-' + cleanSlug + ' PointSystemUserTitle--inProfile';
    // Use textContent (NOT innerHTML) — the title text is user-authored but
    // sanitised server-side; defence-in-depth, never render it as HTML.
    chip.textContent = String(text);
    nameEl.appendChild(chip);
  };

  /*
   * O hero do Avocado foi reescrito (layout estilo Facebook) e a faixa
   * `.AvocadoUserPage-hero-stats`, onde a pílula de saldo morava, deixou de
   * existir — o saldo simplesmente sumia do perfil, sem erro nenhum no
   * console. O lugar equivalente agora é `.AvocadoUserPage-hero-meta`, a
   * linha de "Membro desde…" / "visto há…".
   *
   * O seletor antigo fica junto de propósito: `onAdded` é um observador de
   * DOM, casar com nada não custa nada, e quem ainda estiver numa versão
   * anterior do tema continua vendo o saldo.
   */
  if (showPoints) onAdded('.AvocadoUserPage-hero-meta, .AvocadoUserPage-hero-stats', injectPoints);
  if (showName) onAdded('.AvocadoUserPage-hero-name', tagName);
  if (showTitle) onAdded('.AvocadoUserPage-hero-name', injectTitle);
  if (showCover) {
    /*
     * O hero é montado DUAS vezes: primeiro o esqueleto
     * (`AvocadoUserPage-hero--skeleton`), depois o real quando o usuário
     * chega na store. Na segunda vez o Mithril faz patch da MESMA <div> —
     * mesma tag, mesmo lugar — então o observador de `addedNodes` não vê
     * nada e o injetor não roda de novo. Na primeira o usuário ainda não
     * existe, o injetor desiste, e a capa equipada nunca aparecia.
     *
     * Nem todo filho serve de gatilho: o esqueleto já traz `-hero-inner`,
     * `-hero-bar` e `-hero-identity`, que portanto também são patcheados.
     * Os nós abaixo só existem no render real (o esqueleto põe `-shimmer--*`
     * no lugar deles), então são nós NOVOS de verdade — o observador vê, e
     * `closest()` devolve o hero já com o usuário na store. O seletor do
     * próprio hero fica junto para o caso de ele nascer pronto.
     */
    onAdded('.AvocadoUserPage-hero', injectCover);
    onAdded('.AvocadoUserPage-hero-nameRow, .AvocadoUserPage-hero-avatarWrap, .AvocadoUserPage-hero-meta', el => {
      const hero = el.closest('.AvocadoUserPage-hero');
      if (hero) injectCover(hero);
    });
    // Also cover Flarum core's UserPage hero (used on default theme + many
    // other themes). UserCard is handled by our `extend(UserCard, 'view')`.
    onAdded('.UserPage .Hero, .UserHero', injectCover);
  }
}

// Push `ps-name-{slug}` onto an array of CSS classes if the user has a
// decoration equipped. Used by the CommentPost.classes() extension where the
// component exposes its class list as an array.
function pushDecoClass(classes, user) {
  var _user$attribute11;
  const slug = user == null || (_user$attribute11 = user.attribute) == null ? void 0 : _user$attribute11.call(user, 'equippedNameDecorationSlug');
  if (!slug) return;
  const cleanSlug = String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
  if (!cleanSlug) return;
  const target = 'ps-name-' + cleanSlug;
  if (!classes.includes(target)) classes.push(target);
}
function pushPostHlClass(classes, user) {
  var _user$attribute12;
  const slug = user == null || (_user$attribute12 = user.attribute) == null ? void 0 : _user$attribute12.call(user, 'equippedPostHighlightDecorationSlug');
  if (!slug) return;
  const cleanSlug = String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
  if (!cleanSlug) return;
  const target = 'ps-posthl-' + cleanSlug;
  if (!classes.includes(target)) classes.push(target);
}
function cssClass(slug) {
  return String(slug).replace(/[^a-zA-Z0-9_-]/g, '');
}
function resolveAssetUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = app.forum.attribute('assetsBaseUrl') || app.forum.attribute('baseUrl') + '/assets';
  return base.replace(/\/+$/, '') + '/' + String(path).replace(/^\/+/, '');
}